import React, { useState, useEffect } from "react";
import apiService from "../services/apiService";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

/* global Office */

const CaseSuccess = ({
    caseResult,
    fields,
    filledCount,
    totalFields,
    dynamicMissing,
    onStartOver,
    sfContext,
    followupDraft,
    draftLoading,
    updateFollowupDraft
}) => {
    const [copied, setCopied] = useState(false);

    const editor = useEditor({
        extensions: [StarterKit],
        content: followupDraft || "", 
        onUpdate: ({ editor }) => {
            updateFollowupDraft(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'followup-draft-body rich-editor',
            },
        },
    });

    // Handle initial loading of the draft from Parent
    useEffect(() => {
        if (editor && followupDraft && editor.isEmpty) {
            // If the draft starts with '<p>', it's already HTML from a previous session
            if (followupDraft.startsWith('<p>')) {
                editor.commands.setContent(followupDraft);
            } else {
                // It's the first time and it's plain text from the API
                const htmlContent = followupDraft
                    .split(/\n\n+/)
                    .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
                    .join('');
                editor.commands.setContent(htmlContent);
                // Also update the parent with the HTML version
                updateFollowupDraft(htmlContent);
            }
        }
    }, [followupDraft, editor]);

    const handleInsertDraft = () => {
        try {
            const item = Office.context?.mailbox?.item;
            if (item && editor) {
                const htmlBody = editor.getHTML();
                item.displayReplyAllForm({
                    htmlBody: htmlBody
                });
            }
        } catch (e) {
            console.log("Office.js insert not available");
        }
    };

    const handleCopyToClipboard = () => {
        try {
            if (!editor) return;
            const textarea = document.createElement('textarea');
            textarea.value = editor.getText();
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error("Copy failed:", e);
        }
    };

    if (!editor) {
        return null;
    }

    return (
        <div className="success-section">
            {/* Case Created Card */}
            <div className="success-case-card">
                <div className="success-case-header">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" fill="#059669" />
                        <path d="M7 12.5L10.5 16L17 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h4 className="success-case-title">Case Created!</h4>
                </div>
                <div className="success-case-details">
                    {caseResult?.caseNumber && (
                        <div className="success-detail-row">
                            <span className="success-detail-label">Case #:</span>
                            <span className="success-detail-value">{caseResult.caseNumber}</span>
                        </div>
                    )}
                    <div className="success-detail-row">
                        <span className="success-detail-label">Type:</span>
                        <span className="success-detail-value">{fields.caseType}</span>
                    </div>
                    <div className="success-detail-row">
                        <span className="success-detail-label">Status:</span>
                        <span className="success-detail-value">
                            {caseResult?.status || "New - Awaiting Client Response"}
                        </span>
                    </div>
                    <div className="success-detail-row">
                        <span className="success-detail-label">Fields Populated:</span>
                        <span className="success-detail-value">{filledCount} of {totalFields}</span>
                    </div>
                </div>
            </div>

            {/* Follow-up Email Draft — with Tiptap Editor */}
            {(followupDraft || draftLoading) && (
                <div className="followup-draft-card">
                    <div className="followup-draft-header">
                        <div className="followup-draft-title-group">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2b4a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 2L11 13" />
                                <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                            </svg>
                            <span className="followup-draft-title">Follow-up Email Draft</span>
                        </div>
                        {dynamicMissing.length > 0 && (
                            <span className="followup-missing-badge">Asks for {dynamicMissing.length} missing fields</span>
                        )}
                    </div>

                    {draftLoading ? (
                        <div className="followup-loading">
                            <span className="spinner" /> Generating follow-up email...
                        </div>
                    ) : (
                        <div className="editor-container">
                            {/* Toolbar */}
                            <div className="editor-toolbar">
                                <button 
                                    type="button" 
                                    className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`} 
                                    onClick={() => editor.chain().focus().toggleBold().run()} 
                                    title="Bold"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><line x1="6" y1="4" x2="6" y2="20"/>
                                    </svg>
                                </button>
                                <button 
                                    type="button" 
                                    className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`} 
                                    onClick={() => editor.chain().focus().toggleItalic().run()} 
                                    title="Italic"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/>
                                    </svg>
                                </button>
                                <button 
                                    type="button" 
                                    className={`toolbar-btn ${editor.isActive('strike') ? 'is-active' : ''}`} 
                                    onClick={() => editor.chain().focus().toggleStrike().run()} 
                                    title="Strikethrough"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12"/><path d="M16 4H9a4 4 0 0 0-4 4 4 4 0 0 0 4 4h6a4 4 0 0 1 4 4 4 4 0 0 1-4 4H7"/>
                                    </svg>
                                </button>
                                <div className="toolbar-divider" />
                                <button 
                                    type="button" 
                                    className={`toolbar-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`} 
                                    onClick={() => editor.chain().focus().toggleBulletList().run()} 
                                    title="Bullet List"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                                    </svg>
                                </button>
                                <button 
                                    type="button" 
                                    className={`toolbar-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`} 
                                    onClick={() => editor.chain().focus().toggleOrderedList().run()} 
                                    title="Numbered List"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4H4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>
                                    </svg>
                                </button>
                            </div>

                            {/* Tiptap Content Area */}
                            <EditorContent editor={editor} className="rich-editor-content-wrapper" />
                        </div>
                    )}
                </div>
            )}

            {/* Action Buttons */}
            <div className="followup-actions">
                {followupDraft && (
                    <>
                        <button className="followup-insert-btn" onClick={handleInsertDraft}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                            Insert Draft
                        </button>
                        <button className="followup-icon-btn" title="Copy to clipboard" onClick={handleCopyToClipboard}>
                            {copied ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                            )}
                        </button>
                    </>
                )}
                <button
                    className={followupDraft ? 'followup-icon-btn' : 'analyze-btn'}
                    title="Start over"
                    onClick={onStartOver}
                >
                    {followupDraft ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                        </svg>
                    ) : (
                        "Analyze Another Email"
                    )}
                </button>
            </div>
        </div>
    );
};

export default CaseSuccess;
